import React from "react";
import Header from "../../components/Header";
import DashboardSidebar from "../../components/DashboardSidebar";
import { getAuth, onAuthStateChanged, signOut } from 'firebase/auth';
import { getDatabase, ref, get } from "firebase/database";
import { useParams, useNavigate } from "react-router-dom";
import { IconContext } from "react-icons";
import { FiUsers, FiClock, FiTrendingUp, FiCode, FiStar, FiDownload, FiFileText, FiChevronDown, FiChevronUp } from "react-icons/fi";
import "./CandidateResults.css";
import { CandidateResultsProps, CandidateResultsState, CandidateData, CandidateStats, User, TranscriptExportMessage } from "../../types";

// Create a wrapper component to use hooks
function CandidateResultsWrapper(props: { darkMode: boolean }) {
    const params = useParams<{ interviewId: string }>();
    const navigate = useNavigate();
    return <CandidateResults {...props} interviewId={params.interviewId || ''} navigate={navigate} />;
}

class CandidateResults extends React.Component<CandidateResultsProps, CandidateResultsState> {
    private auth = getAuth();
    private db = getDatabase();

    constructor(props: CandidateResultsProps) {
        super(props);
        this.state = {
            user: null,
            login: false,
            loading: true,
            interview: null,
            candidates: [],
            companyInfo: null,
            stats: {
                totalCandidates: 0,
                completedInterviews: 0,
                averageScore: 0,
                averageTime: 0
            }
        };

        this.loadInterviewData = this.loadInterviewData.bind(this);
        this.logout = this.logout.bind(this);
        this.exportTranscript = this.exportTranscript.bind(this);
    }

    componentDidMount(): void {
        onAuthStateChanged(this.auth, (user) => {
            if (user) {
                this.setState({
                    user: user as User,
                    login: true,
                    loading: false
                }, () => {
                    this.loadInterviewData();
                });
            } else {
                this.setState({
                    login: false,
                    loading: false
                });
                this.props.navigate("/login");
            }
        });
    }

    logout(): void {
        signOut(this.auth).then(() => {
            this.props.navigate("/login");
        });
    }

    async loadInterviewData(): Promise<void> {
        const userId = this.state.user?.uid;
        if (!userId) return;

        const interviewId = this.props.interviewId;

        try {
            // Load company info for sidebar
            const userRef = ref(this.db, `users/${userId}`);
            const userSnapshot = await get(userRef);
            if (userSnapshot.exists()) {
                this.setState({ companyInfo: userSnapshot.val() });
            }

            // Load interview details
            const interviewRef = ref(this.db, `company-interviews/${userId}/${interviewId}`);
            const interviewSnapshot = await get(interviewRef);

            if (interviewSnapshot.exists()) {
                this.setState({ interview: interviewSnapshot.val() });
            }

            // Load candidate responses
            const candidatesRef = ref(this.db, `candidate-responses/${interviewId}`);
            const candidatesSnapshot = await get(candidatesRef);

            const candidates: CandidateData[] = [];
            let stats: CandidateStats = {
                totalCandidates: 0,
                completedInterviews: 0,
                averageScore: 0,
                averageTime: 0
            };

            if (candidatesSnapshot.exists()) {
                const candidatesData = candidatesSnapshot.val();
                Object.keys(candidatesData).forEach(candidateId => {
                    const candidateData = candidatesData[candidateId];
                    const candidate: CandidateData = {
                        id: candidateId,
                        name: candidateData.name || 'Anonymous',
                        email: candidateData.email || '',
                        score: candidateData.score || 0,
                        timeSpent: candidateData.timeSpent || 0,
                        completedAt: candidateData.completedAt || '',
                        code: candidateData.code || '',
                        language: candidateData.language || 'Unknown',
                        status: candidateData.status || 'completed',
                        notes: candidateData.notes || ''
                    };
                    candidates.push(candidate);
                    stats.totalCandidates++;

                    if (candidate.status === 'completed') {
                        stats.completedInterviews++;
                    }
                });

                // Calculate averages
                if (stats.completedInterviews > 0) {
                    const totalScore = candidates
                        .filter(c => c.status === 'completed')
                        .reduce((sum, c) => sum + c.score, 0);
                    stats.averageScore = Math.round(totalScore / stats.completedInterviews);

                    const totalTime = candidates
                        .filter(c => c.status === 'completed')
                        .reduce((sum, c) => sum + c.timeSpent, 0);
                    stats.averageTime = Math.round(totalTime / stats.completedInterviews);
                }
            }

            // Sort candidates by score (highest first)
            candidates.sort((a, b) => b.score - a.score);

            this.setState({ candidates, stats });

        } catch (error) {
            console.error('Error loading interview data:', error);
        }
    }

    formatTime(minutes: number): string {
        if (minutes < 60) {
            return `${minutes}m`;
        }
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return `${hours}h ${mins}m`;
    }

    getScoreColor(score: number): string {
        if (score >= 80) return '#00ff00';
        if (score >= 60) return '#ffa500';
        return '#ff0000';
    }

    exportResults(): void {
        const { interview, candidates } = this.state;
        const csvContent = [
            ['Candidate Name', 'Email', 'Score', 'Time Spent', 'Language', 'Status', 'Completed At'].join(','),
            ...candidates.map(candidate => [
                candidate.name,
                candidate.email,
                candidate.score,
                candidate.timeSpent,
                candidate.language,
                candidate.status,
                candidate.completedAt
            ].join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${interview?.title || 'interview'}-results.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
    }

    exportTranscript(candidate: CandidateData): void {
        if (!candidate.transcript || candidate.transcript.length === 0) return;
        const lines = candidate.transcript.map((msg: TranscriptExportMessage) => {
            const time = new Date(msg.timestamp).toLocaleString();
            const speaker = msg.sender === 'user' ? 'Candidate' : 'Interviewer';
            return `**${speaker}** (${time}):\n${msg.text}\n`;
        });
        const content = `# Interview Transcript — ${candidate.name}\n\nDate: ${candidate.completedAt ? new Date(candidate.completedAt).toLocaleDateString() : 'N/A'}\n\n---\n\n${lines.join('\n')}`;
        const blob = new Blob([content], { type: 'text/markdown' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `transcript-${candidate.name.replace(/\s+/g, '-')}.md`;
        a.click();
        window.URL.revokeObjectURL(url);
    }

    static readonly SCORE_LABELS: Record<string, string> = {
        problemUnderstanding: 'Understanding',
        approach: 'Approach',
        implementation: 'Implementation',
        complexityAnalysis: 'Complexity',
        communication: 'Communication',
        recovery: 'Recovery',
        requirementsClarification: 'Requirements',
        architectureDesign: 'Architecture',
        scalability: 'Scalability',
        tradeOffs: 'Trade-offs',
        diagramQuality: 'Diagram',
    };

    renderScoreBadge(label: string, score: number, evidence?: string): React.ReactElement {
        const color = score >= 4 ? '#00ff00' : score >= 3 ? '#ffa500' : '#ff4444';
        return (
            <div className="score-badge" key={label} title={evidence || undefined}>
                <span className="score-badge-label">{label}</span>
                <span className="score-badge-value" style={{ color }}>{score}/5</span>
                {evidence && <span className="score-badge-evidence" title={evidence}>ⓘ</span>}
            </div>
        );
    }

    renderScores(scores: Record<string, unknown>): React.ReactElement[] {
        const exclude = new Set(['overall', 'summary']);
        const elements: React.ReactElement[] = [];
        for (const [key, val] of Object.entries(scores)) {
            if (exclude.has(key) || key.endsWith('Evidence')) continue;
            const num = typeof val === 'number' ? val : null;
            if (num == null || num < 1 || num > 5) continue;
            const label = CandidateResults.SCORE_LABELS[key] || key.replace(/([A-Z])/g, ' $1').trim();
            const evidence = scores[`${key}Evidence`] as string | undefined;
            elements.push(this.renderScoreBadge(label, num, evidence));
        }
        return elements;
    }

    renderCandidateCard(candidate: CandidateData): React.ReactElement {
        const { darkMode } = this.props;
        const scoreColor = this.getScoreColor(candidate.score);

        return (
            <div key={candidate.id} className={`candidate-card ${darkMode ? 'candidate-card-dark' : ''}`}>
                <div className="candidate-header">
                    <div className="candidate-info">
                        <h3>{candidate.name}</h3>
                        <p>{candidate.email}</p>
                    </div>
                    <div className="candidate-score" style={{ color: scoreColor }}>
                        <FiStar />
                        <span>{candidate.score}%</span>
                    </div>
                </div>

                <div className="candidate-stats">
                    <div className="stat-item">
                        <FiClock />
                        <span>{this.formatTime(candidate.timeSpent)}</span>
                    </div>
                    <div className="stat-item">
                        <FiCode />
                        <span>{candidate.language}</span>
                    </div>
                    <div className="stat-item">
                        <span className={`status-badge ${candidate.status}`}>
                            {candidate.status}
                        </span>
                    </div>
                </div>

                {candidate.code && (
                    <div className="candidate-code">
                        <h4>Code Solution:</h4>
                        <pre>{candidate.code.substring(0, 200)}...</pre>
                    </div>
                )}

                {candidate.notes && (
                    <div className="candidate-notes">
                        <h4>Notes:</h4>
                        <p>{candidate.notes}</p>
                    </div>
                )}

                {/* AI Scores */}
                {candidate.scores && (
                    <div className="candidate-scores-section">
                        <h4>AI Evaluation:</h4>
                        <div className="score-badges-grid">
                            {this.renderScores(candidate.scores as Record<string, unknown>)}
                        </div>
                        {candidate.scores.summary && (
                            <p className="candidate-summary-text">{candidate.scores.summary}</p>
                        )}
                    </div>
                )}

                {/* Transcript & Export */}
                <div className="candidate-actions">
                    {candidate.transcript && candidate.transcript.length > 0 && (
                        <button
                            className={`transcript-btn ${darkMode ? 'transcript-btn-dark' : ''}`}
                            onClick={() => this.exportTranscript(candidate)}
                        >
                            <FiFileText /> Download Transcript
                        </button>
                    )}
                </div>

                <div className="candidate-footer">
                    <span className="completed-at">
                        Completed: {new Date(candidate.completedAt).toLocaleString()}
                    </span>
                </div>
            </div>
        );
    }

    renderStats(): React.ReactElement {
        const { stats, interview } = this.state;
        const { darkMode } = this.props;

        return (
            <div className={`stats-container ${darkMode ? 'stats-container-dark' : ''}`}>
                <div className="interview-header">
                    <h2>{interview?.title || 'Interview Results'}</h2>
                    <p>Candidate performance and interview analytics</p>
                </div>

                <div className="stats-grid">
                    <div className={`stat-card ${darkMode ? 'stat-card-dark' : ''}`}>
                        <FiUsers className="stat-icon" />
                        <h3>{stats.totalCandidates}</h3>
                        <p>Total Candidates</p>
                    </div>
                    <div className={`stat-card ${darkMode ? 'stat-card-dark' : ''}`}>
                        <FiTrendingUp className="stat-icon" />
                        <h3>{stats.completedInterviews}</h3>
                        <p>Completed</p>
                    </div>
                    <div className={`stat-card ${darkMode ? 'stat-card-dark' : ''}`}>
                        <FiStar className="stat-icon" />
                        <h3>{stats.averageScore}%</h3>
                        <p>Average Score</p>
                    </div>
                    <div className={`stat-card ${darkMode ? 'stat-card-dark' : ''}`}>
                        <FiClock className="stat-icon" />
                        <h3>{this.formatTime(stats.averageTime)}</h3>
                        <p>Avg Time</p>
                    </div>
                </div>
            </div>
        );
    }

    render(): React.ReactElement {
        const { darkMode } = this.props;
        const { login, loading, candidates, interview } = this.state;

        if (!login) {
            return <div></div>;
        }

        if (loading) {
            return (
                <div className={`candidate-results-wrapper ${darkMode ? 'candidate-results-dark' : ''}`}>
                    <DashboardSidebar
                        darkMode={darkMode}
                        accountType="company"
                        companyName={this.state.companyInfo?.companyName}
                        userName={this.state.user?.displayName || this.state.user?.email?.split('@')[0]}
                        profilePhotoUrl={this.state.user?.photoURL ?? undefined}
                        onLogout={this.logout}
                    />
                    <Header darkMode={darkMode} />
                    <div className={`dashboard-loading ${darkMode ? 'dashboard-loading-dark' : ''}`}>
                        <div className="loading-spinner">Loading...</div>
                    </div>
                </div>
            );
        }

        return (
            <div className={`candidate-results-wrapper ${darkMode ? 'candidate-results-dark' : ''}`}>
                <DashboardSidebar
                    darkMode={darkMode}
                    accountType="company"
                    companyName={this.state.companyInfo?.companyName}
                    userName={this.state.user?.displayName || this.state.user?.email?.split('@')[0]}
                    profilePhotoUrl={this.state.user?.photoURL ?? undefined}
                    onLogout={this.logout}
                />
                <Header darkMode={darkMode} />

                <div className="results-container">
                    {/* Stats Section */}
                    {this.renderStats()}

                    {/* Actions */}
                    <div className="actions-section">
                        <button className={`export-btn ${darkMode ? 'export-btn-dark' : ''}`} onClick={this.exportResults}>
                            <FiDownload />
                            Export Results
                        </button>
                    </div>

                    {/* Candidates Section */}
                    <div className={`candidates-section ${darkMode ? 'candidates-section-dark' : ''}`}>
                        <h3>Candidate Results</h3>

                        {candidates.length === 0 ? (
                            <div className={`empty-state ${darkMode ? 'empty-state-dark' : ''}`}>
                                <div className="empty-icon">👥</div>
                                <h3>No candidates yet</h3>
                                <p>Share the interview link with candidates to start collecting results.</p>
                            </div>
                        ) : (
                            <div className="candidates-grid">
                                {candidates.map(candidate => this.renderCandidateCard(candidate))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }
}

export default CandidateResultsWrapper;
